import React, { useState, useRef } from 'react';
import './DocumentUpload.css';

const DocumentUpload = ({ onDocumentsChange, maxFiles = 5, acceptedTypes = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.txt' }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [extractedContent, setExtractedContent] = useState({});
  const fileInputRef = useRef(null);

  const handleFileSelection = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Validate file count
    if (uploadedFiles.length + files.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    // Validate file types
    const allowedExtensions = acceptedTypes.split(',').map(ext => ext.trim().toLowerCase());
    const invalidFiles = files.filter(file => {
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      return !allowedExtensions.includes(extension);
    });

    if (invalidFiles.length > 0) {
      alert(`Invalid file type(s): ${invalidFiles.map(f => f.name).join(', ')}. Allowed types: ${acceptedTypes}`);
      return;
    }

    setIsUploading(true);

    try {
      // Process each file
      const processedFiles = [];
      const contentResults = {};

      for (const file of files) {
        const fileData = {
          id: Date.now() + Math.random(),
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          status: 'processing'
        };
        processedFiles.push(fileData);

        // Extract text content from the file
        try {
          const extractedText = await extractTextFromFile(file);
          contentResults[fileData.id] = extractedText;
          fileData.status = 'completed';
          fileData.extractedText = extractedText;
        } catch (error) {
          console.error('Error extracting text from file:', error);
          fileData.status = 'error';
          fileData.error = 'Failed to extract text from file';
        }
      }

      const updatedFiles = [...uploadedFiles, ...processedFiles];
      const updatedContent = { ...extractedContent, ...contentResults };

      setUploadedFiles(updatedFiles);
      setExtractedContent(updatedContent);

      // Notify parent component
      if (onDocumentsChange) {
        onDocumentsChange(updatedFiles, updatedContent);
      }

    } catch (error) {
      console.error('Error processing files:', error);
      alert('Error processing files. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const extractTextFromFile = async (file) => {
    return new Promise((resolve, reject) => {
      const fileType = file.type;
      const fileName = file.name.toLowerCase();

      if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
        extractTextFromPDF(file, resolve, reject);
      } else if (fileType.startsWith('image/') || fileName.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)) {
        extractTextFromImage(file, resolve, reject);
      } else if (fileType === 'text/plain' || fileName.endsWith('.txt')) {
        extractTextFromTextFile(file, resolve, reject);
      } else if (fileName.match(/\.(doc|docx)$/)) {
        // For Word documents, we'll send to backend for processing
        extractTextFromWordDocument(file, resolve, reject);
      } else {
        reject(new Error('Unsupported file type'));
      }
    });
  };

  const extractTextFromPDF = (file, resolve, reject) => {
    // For now, we'll send PDF to backend for processing
    // In a real implementation, you might use pdf.js
    const formData = new FormData();
    formData.append('file', file);
    
    fetch('/api/extract-text/pdf', {
      method: 'POST',
      body: formData
    })
    .then(response => response.json())
    .then(data => resolve(data.text || ''))
    .catch(error => {
      console.error('PDF extraction error:', error);
      resolve(`[PDF Document: ${file.name} - Text extraction pending]`);
    });
  };

  const extractTextFromImage = (file, resolve, reject) => {
    // For now, we'll send image to backend for OCR processing
    const formData = new FormData();
    formData.append('file', file);
    
    fetch('/api/extract-text/image', {
      method: 'POST',
      body: formData
    })
    .then(response => response.json())
    .then(data => resolve(data.text || ''))
    .catch(error => {
      console.error('Image OCR error:', error);
      resolve(`[Image Document: ${file.name} - Text extraction pending]`);
    });
  };

  const extractTextFromTextFile = (file, resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(new Error('Failed to read text file'));
    reader.readAsText(file);
  };

  const extractTextFromWordDocument = (file, resolve, reject) => {
    // Send Word document to backend for processing
    const formData = new FormData();
    formData.append('file', file);
    
    fetch('/api/extract-text/word', {
      method: 'POST',
      body: formData
    })
    .then(response => response.json())
    .then(data => resolve(data.text || ''))
    .catch(error => {
      console.error('Word document extraction error:', error);
      resolve(`[Word Document: ${file.name} - Text extraction pending]`);
    });
  };

  const removeFile = (fileId) => {
    const updatedFiles = uploadedFiles.filter(f => f.id !== fileId);
    const updatedContent = { ...extractedContent };
    delete updatedContent[fileId];
    
    setUploadedFiles(updatedFiles);
    setExtractedContent(updatedContent);
    
    if (onDocumentsChange) {
      onDocumentsChange(updatedFiles, updatedContent);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="document-upload-container">
      <div className="upload-section">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelection}
          multiple
          accept={acceptedTypes}
          style={{ display: 'none' }}
        />
        
        <div className="upload-area" onClick={() => fileInputRef.current?.click()}>
          <div className="upload-icon">📄</div>
          <div className="upload-text">
            <p><strong>Click to upload documents</strong></p>
            <p>Support for PDF, Word, Images, and Text files</p>
            <p>Maximum {maxFiles} files, 10MB each</p>
          </div>
        </div>
      </div>

      {isUploading && (
        <div className="upload-progress">
          <div className="spinner"></div>
          <p>Processing documents and extracting text...</p>
        </div>
      )}

      {uploadedFiles.length > 0 && (
        <div className="uploaded-files">
          <h4>Uploaded Documents ({uploadedFiles.length}/{maxFiles})</h4>
          <div className="files-list">
            {uploadedFiles.map(file => (
              <div key={file.id} className={`file-item ${file.status}`}>
                <div className="file-info">
                  <div className="file-name">{file.name}</div>
                  <div className="file-details">
                    {formatFileSize(file.size)} • {file.status}
                  </div>
                  {file.extractedText && (
                    <div className="extracted-preview">
                      <details>
                        <summary>View extracted content ({file.extractedText.length} characters)</summary>
                        <div className="extracted-text">
                          {file.extractedText.substring(0, 300)}
                          {file.extractedText.length > 300 ? '...' : ''}
                        </div>
                      </details>
                    </div>
                  )}
                  {file.error && (
                    <div className="file-error">{file.error}</div>
                  )}
                </div>
                <div className="file-actions">
                  {file.status === 'processing' && <div className="mini-spinner"></div>}
                  {file.status === 'completed' && <div className="status-icon">✅</div>}
                  {file.status === 'error' && <div className="status-icon">❌</div>}
                  <button 
                    type="button"
                    onClick={() => removeFile(file.id)}
                    className="remove-file-btn"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
